import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { NavigationBreadcrumb } from '../common/navigation-breadcrumbs/navigation-breadcrumbs.component';
import { ListConfig } from '../list/list.types';
import { PanelEntity, PanelEntityType } from './panel.types';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  BooksService,
  CoursesService,
  ExamsService,
  FlashCardsService,
  FlashCardStacksService,
  LiveEventsService,
  QuestionsService,
  QuizzesService,
  TagsService,
} from 'src/app/api/services';
import { DialogService } from 'src/app/cms/common/dialog.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { getQuizListConfig } from '../list/quiz-list.component';
import { getQuestionListConfig } from '../list/question-list.component';
import { getFlashCardStackListConfig } from '../list/flash-card-stack-list.component';
import { getFlashCardListConfig } from '../list/flash-card-list.component';
import {
  Exam,
  FlashCardModel,
  FlashCardStackModel,
  LiveEvent,
  QuestionModel,
  QuizModel,
} from 'src/app/api/models';
import { forkJoin, from, Observable, of, tap } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { animate, style, transition, trigger } from '@angular/animations';
import { UserJourneyService } from '../pages/onboarding-steps/user-journey.service';
import { convertQuizToEvent } from '../common/convertQuizToEvent';
import { getLiveEventListConfig } from '../live-event/cms-live-event-select-event.component';
import { AuthService } from 'src/app/auth/auth.service';
import { PanelService } from './panel.service';
import { getExamConfig } from '../exam/cms-exam-select-exam.component';
import { Location, NgClass } from '@angular/common';
import { CopyLinkDialogComponent } from './copy-link-dialog/copy-link-dialog.component';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { GridDataComponent } from '../list/grid-data/grid-data.component';
import { EditService } from '../edit/edit.service';
import * as _ from 'lodash';
import { SubscriptionLimitService } from '../common/dialogs/subscription-limit-warning/subscription-limit.service';
import {
  MatSidenavContainer,
  MatSidenav,
  MatSidenavContent,
} from '@angular/material/sidenav';
import { ContentSpinnerComponent } from '../common/ui/content-spinner.component';
import {
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { NgScrollbar } from 'ngx-scrollbar';
import { QuestionMockPhoneComponent } from '../edit/question-mock-phone.component';
import { FlashCardMockPhoneComponent } from '../edit/flash-card-mock-phone.component';

export interface PanelImportItemsInput {
  parentId: number;
  type: PanelEntityType;
}

@Component({
  selector: 'qa-panel-import-items',
  templateUrl: './panel-import-items.component.html',
  styleUrls: ['./panel-import-items.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('parent', [transition(':enter', [])]),
    trigger('enterAnimation', [
      transition(':enter', [
        style({ transform: 'translateX(100%)', opacity: 0 }),
        animate('300ms', style({ transform: 'translateX(0)', opacity: 1 })),
      ]),
      transition(':leave', [
        style({ transform: 'translateX(0)', opacity: 1 }),
        animate('300ms', style({ transform: 'translateX(100%)', opacity: 0 })),
      ]),
    ]),
  ],
  imports: [
    MatSidenavContainer,
    MatSidenav,
    MatSidenavContent,
    ContentSpinnerComponent,
    GridDataComponent,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatIconButton,
    MatIcon,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    NgClass,
    ButtonComponent,
    NgScrollbar,
    QuestionMockPhoneComponent,
    FlashCardMockPhoneComponent,
    TranslateModule,
  ],
})
export class PanelImportItemsComponent implements OnInit, OnDestroy {
  opened = false;
  navigationBreadcrumb: NavigationBreadcrumb = {
    homeUrl: '/cms/dashboard',
    currentPage: 'Import existing questions',
    breadcrumbs: [],
  };
  listConfig: ListConfig<PanelEntity>;
  loading = true;
  selectedItemsToImport: PanelEntity[] = [];
  selectedItem: PanelEntity;
  input: PanelImportItemsInput;
  isShowingLargeJoinInfo = false;
  panelEntityType = PanelEntityType;
  courseId: number;
  bookId: number;
  liveEventId: number;
  examPlayId: number;
  importedItems = [];
  selectedRows = [];
  convertToEvent = false;

  get optionsDisabled() {
    return (
      this.convertToEvent ||
      this.input.type === PanelEntityType.Exam ||
      this.input.type === PanelEntityType.Event
    );
  }

  @ViewChild('list') listRef: GridDataComponent;

  constructor(
    private courses: CoursesService,
    private books: BooksService,
    private dialog: DialogService,
    private matDialog: MatDialog,
    private exams: ExamsService,
    private flashCards: FlashCardsService,
    private flashCardStacks: FlashCardStacksService,
    private matSnackBar: MatSnackBar,
    private router: Router,
    private questions: QuestionsService,
    private quizzes: QuizzesService,
    private liveEventService: LiveEventsService,
    private route: ActivatedRoute,
    private translator: TranslateService,
    private tagsService: TagsService,
    private userJourney: UserJourneyService,
    private auth: AuthService,
    private panelService: PanelService,
    private location: Location,
    private editService: EditService,
    private subscriptionLimit: SubscriptionLimitService
  ) {
    route.queryParams.subscribe((params) => {
      if (params.parentId || params.type) {
        this.input = {
          parentId: params?.parentId,
          type: params.type,
        };
        if (params.courseId) {
          this.courseId = params.courseId;
        }
        if (params.bookId) {
          this.bookId = params.bookId;
        }
        if (params.liveEventId) {
          this.liveEventId = params.liveEventId;
        }
        if (params.from === 'play' && params.exam) {
          this.examPlayId = params.exam;
        }
        if (params.toLiveEvent) {
          this.convertToEvent = true;
        }
      }
    });
  }

  ngOnInit(): void {
    this.initializeList();
    this.toggleSidebar('none');
  }

  ngOnDestroy(): void {
    this.toggleSidebar('flex');
  }

  get displayedColumnNames() {
    const columns = this.listRef?.displayedColumnNames;
    if (columns) {
      columns.shift();
    }
    return columns || [];
  }

  initializeList() {
    let listConfig: ListConfig<any>;
    if (!this.input) {
      return;
    }
    this.getImportedItems();
    switch (this.input.type) {
      case PanelEntityType.Quiz:
        listConfig = getQuizListConfig(
          this.quizzes,
          this.matDialog,
          this.dialog,
          this.matSnackBar,
          this.translator,
          true,
          this.router,
          this.editService
        );
        break;
      case PanelEntityType.Event:
        listConfig = getLiveEventListConfig(
          this.liveEventService,
          this.matDialog,
          this.dialog,
          this.matSnackBar,
          this.translator,
          this.router,
          this.auth,
          this.panelService,
          this.userJourney,
          this.editService,
          this.subscriptionLimit
        );
        break;
      case PanelEntityType.Exam:
        listConfig = getExamConfig(
          this.exams,
          this.router,
          this.translator,
          this.panelService,
          this.matSnackBar,
          this.dialog,
          this.userJourney,
          this.editService,
          this.subscriptionLimit
        );
        break;
      case PanelEntityType.Question:
      case PanelEntityType.EventQuestion:
      case PanelEntityType.ExamQuestion:
        listConfig = getQuestionListConfig(
          this.questions,
          this.dialog,
          this.matSnackBar,
          this.router,
          this.translator,
          this.matDialog,
          false,
          this.tagsService
        );
        break;
      case PanelEntityType.FlashCardStack:
        listConfig = getFlashCardStackListConfig(
          this.flashCardStacks,
          this.matDialog,
          this.dialog,
          this.matSnackBar,
          this.translator,
          this.router,
          false,
          this.editService
        );
        break;
      case PanelEntityType.FlashCard:
        listConfig = getFlashCardListConfig(
          this.flashCards,
          this.router,
          this.dialog,
          this.matSnackBar,
          this.translator,
          this.matDialog,
          false,
          this.tagsService,
          this.auth
        );
        break;
      default:
        throw new Error(`Invalid input for "type": ${this.input.type}`);
    }
    listConfig.hideWrapper = true;
    delete listConfig.createButton;
    listConfig.activeColumnNames = listConfig.activeColumnNames.filter(
      (col) => col !== 'num_answers'
    );
    if (
      this.input.type === PanelEntityType.Exam ||
      this.input.type === PanelEntityType.Event
    ) {
      listConfig.activeActionNames.pop();
      if (listConfig?.chip) {
        delete listConfig.chip;
      }
    }
    listConfig.columns = listConfig.columns
      .filter((col) => col.name !== 'num_answers')
      .map((column) => {
        if (this.input.type !== PanelEntityType.Exam) {
          if (column.name === 'text') {
            column.click = async (meta) => {
              if (listConfig.fetchRowItemById) {
                this.loading = true;
                const item: PanelEntity = await this.getItemById(
                  listConfig,
                  meta.row.id
                );
                if (
                  this.input.type === PanelEntityType.Question ||
                  this.input.type === PanelEntityType.FlashCard
                ) {
                  listConfig.selectedEntityId = item.id;
                }
                this.selectedItem = item;
                this.isShowingLargeJoinInfo = true;
                this.loading = false;
              } else {
                this.selectedItem = meta.row;
                this.isShowingLargeJoinInfo = true;
              }
            };
          } else if (column.name === 'name') {
            column.click = (meta) => {
              this.setRedirect(meta.row);
            };
          } else {
            delete column.click;
          }
        }
        if (this.input.type === PanelEntityType.Question) {
          if (column.name === 'text') {
            column.icon = (meta) => {
              if (meta.row.is_poll_question) {
                return 'analytics';
              } else {
                return null;
              }
            };
          }
        }
        delete column.link;
        delete column.toggle;
        if (
          this.input.type === PanelEntityType.Question ||
          this.input.type === PanelEntityType.ExamQuestion ||
          this.input.type === PanelEntityType.EventQuestion
        ) {
          const names = [
            'has_explanation',
            'has_weblink',
            'is_poll_question',
            'has_image',
          ];
          if (names.includes(column.name as string)) {
            delete column.filter;
            delete column.filterOptions;
          }
        }
        if (column.name === 'type') {
          if (this.input.type === PanelEntityType.EventQuestion) {
            column.filterOptionsByGroup[0].group.pop();
          }
          if (this.input.type === PanelEntityType.Question) {
            column.filterOptionsByGroup[0].group.pop();
            column.filterOptionsByGroup.pop();
          }
        }
        return column;
      });
    listConfig.actionsMenu = null;
    listConfig.actions = [];
    listConfig.multiselectActions = [];
    if (
      this.input.type === PanelEntityType.Question ||
      this.input.type === PanelEntityType.ExamQuestion ||
      this.input.type === PanelEntityType.EventQuestion
    ) {
      listConfig.query.searchColumnName = 'text';
      let filter = {};
      if (
        !this.liveEventId &&
        this.input.type !== PanelEntityType.EventQuestion
      ) {
        if (this.input.type === PanelEntityType.Question) {
          filter = { question_type: [0, 1, 2], poll_type: [] };
        }
      }
      if (this.input.type === PanelEntityType.EventQuestion) {
        filter['is_available_for_live_event'] = true;
      }
      listConfig.query.initial.filters = filter;
    }
    this.listConfig = listConfig;
    this.loading = false;
  }

  checkDisabled(meta): boolean {
    const entity = meta && meta.row ? meta.row : meta;
    const queryParams = this.route.snapshot['queryParams'];
    if (queryParams.type === PanelEntityType.Question && this.liveEventId) {
      if (meta.row && !meta.row.is_poll_question && meta.row.type === 3) {
        return true;
      }
    }
    return !!this.importedItems.find((item) => item.id === entity.id);
  }

  isChecked(meta): boolean {
    const entity = meta && meta.row ? meta.row : meta;
    const queryParams = this.route.snapshot['queryParams'];
    if (queryParams.type === PanelEntityType.Question && this.liveEventId) {
      if (meta.row && !meta.row.is_poll_question && meta.row.type === 3) {
        return true;
      }
    }
    return !!this.selectedItemsToImport.find((item) => item.id === entity.id);
  }

  getItemById(config: ListConfig, id: number) {
    return config.fetchRowItemById(id).toPromise();
  }

  toggleSidebar(display: 'none' | 'flex') {
    const sidebar = document.getElementsByClassName('qa-sidebar-inner');
    if (sidebar && sidebar.length) {
      const sidenav = sidebar[0] as HTMLElement;
      sidenav.style.display = display;
    }
  }

  setRedirect(entity: PanelEntity) {
    this.loading = true;
    const queryParams = this.route.snapshot['queryParams'];
    if (queryParams.type === PanelEntityType.Quiz) {
      this.quizzes
        .getQuestionsForQuiz({
          embed_answers: false,
          id: entity.id,
        })
        .subscribe(
          (questions: QuestionModel[]) => {
            this.loading = false;
            if (questions.length) {
              this.router.navigate(
                [`/cms/edit/${this.panelEntityType.Question}`, questions[0].id],
                { queryParams: { course: this.courseId, quiz: entity.id } }
              );
            }
          },
          () => (this.loading = false)
        );
    } else {
      this.flashCardStacks
        .getCardsInFlashCardStack({
          id: entity.id,
        })
        .subscribe(
          (cards: FlashCardModel[]) => {
            this.loading = false;
            if (cards.length) {
              this.router.navigate(
                [`/cms/edit/${this.panelEntityType.FlashCard}`, cards[0].id],
                {
                  queryParams: {
                    course: this.courseId,
                    'flash-card-stack': entity.id,
                  },
                }
              );
            }
          },
          () => (this.loading = false)
        );
    }
  }

  setSelectedItems(rows: any) {
    this.importedItems = this.importedItems ?? [];
    if (this.importedItems) {
      this.selectedItemsToImport = rows.filter((el) => {
        return this.importedItems.map((i) => i.id).indexOf(el.id) < 0;
      });
    }
  }

  removeSelection(item: PanelEntity) {
    this.listRef.checkRow(item, { checked: false } as MatCheckboxChange);
  }

  displayItem(item: PanelEntity): string {
    switch (this.input.type) {
      case PanelEntityType.Quiz:
        return (item as QuizModel).name;
      case PanelEntityType.Event:
        return (item as LiveEvent).name;
      case PanelEntityType.Exam:
        return (item as Exam).name;
      case PanelEntityType.Question:
      case PanelEntityType.ExamQuestion:
      case PanelEntityType.EventQuestion:
        return (item as QuestionModel).text;
      case PanelEntityType.FlashCardStack:
        return (item as FlashCardStackModel).name;
      case PanelEntityType.FlashCard:
        return (item as FlashCardModel).text;
      default:
        throw new Error(`Invalid input for "type": ${this.input.type}`);
    }
  }

  getTitle(): string {
    let entityName: string;
    switch (this.input.type) {
      case PanelEntityType.Quiz:
        entityName = this.translator.instant('common.label.importQuizFrom');
        break;
      case PanelEntityType.Event:
        entityName = this.translator.instant(
          'common.label.importLiveEventFrom'
        );
        break;
      case PanelEntityType.Exam:
        entityName = this.translator.instant('common.label.importExamFrom');
        break;
      case PanelEntityType.Question:
      case PanelEntityType.ExamQuestion:
      case PanelEntityType.EventQuestion:
        entityName = this.translator.instant('common.label.importQuestionFrom');
        break;
      case PanelEntityType.FlashCardStack:
        entityName = this.translator.instant('common.label.importstacksFrom');
        break;
      case PanelEntityType.FlashCard:
        entityName = this.translator.instant(
          'common.label.importFlashCardFrom'
        );
        break;
      default:
        throw new Error(`Invalid input for "type": ${this.input.type}`);
    }
    if (this.courseId) {
      this.navigationBreadcrumb.breadcrumbs = [
        {
          text: this.translator.instant('course.title'),
          route: '/cms/courses',
        },
      ];
    }
    this.navigationBreadcrumb.currentPage =
      this.translator.instant('content.message.importExisting') + entityName;
    return ` ${entityName}`;
  }
  getSelection() {
    let entityName: string;
    switch (this.input.type) {
      case PanelEntityType.Quiz:
        entityName = 'Quiz';
        break;
      case PanelEntityType.Event:
        entityName = this.translator.instant('common.label.liveEvent');
        break;
      case PanelEntityType.Exam:
        entityName = this.translator.instant('exam.titleChallenge');
        break;
      case PanelEntityType.Question:
      case PanelEntityType.ExamQuestion:
      case PanelEntityType.EventQuestion:
        entityName = this.translator.instant('common.label.question');
        break;
      case PanelEntityType.FlashCardStack:
        entityName = this.translator.instant('common.label.flashCardStack');
        break;
      case PanelEntityType.FlashCard:
        entityName = this.translator.instant('common.label.quizFlashCard');
        break;
      default:
        throw new Error(`Invalid input for "type": ${this.input.type}`);
    }
    if (this.courseId) {
      this.navigationBreadcrumb.breadcrumbs = [
        {
          text: this.translator.instant('course.title'),
          route: '/cms/courses',
        },
      ];
    }
    this.navigationBreadcrumb.currentPage =
      this.translator.instant('content.message.importExisting') + entityName;
    return ` ${entityName}`;
  }
  emptySelection(): string {
    let entityName: string;
    switch (this.input.type) {
      case PanelEntityType.Quiz:
        entityName = this.translator.instant('importItemDialog.noQuizSelected');
        break;
      case PanelEntityType.Event:
        entityName = this.translator.instant(
          'importItemDialog.noLiveEventSelected'
        );
        break;
      case PanelEntityType.Exam:
        entityName = this.translator.instant('importItemDialog.noExamSelected');
        break;
      case PanelEntityType.Question:
      case PanelEntityType.ExamQuestion:
      case PanelEntityType.EventQuestion:
        entityName = this.translator.instant(
          'importItemDialog.noQuestionSelected'
        );
        break;
      case PanelEntityType.FlashCardStack:
        entityName = this.translator.instant(
          'importItemDialog.nostackSelected'
        );
        break;
      case PanelEntityType.FlashCard:
        entityName = this.translator.instant(
          'importItemDialog.noFlashCardSelected'
        );
        break;
      default:
        throw new Error(`Invalid input for "type": ${this.input.type}`);
    }
    if (this.courseId) {
      this.navigationBreadcrumb.breadcrumbs = [
        {
          text: this.translator.instant('course.title'),
          route: '/cms/courses',
        },
      ];
    }
    this.navigationBreadcrumb.currentPage =
      this.translator.instant('content.message.importExisting') + entityName;
    return ` ${entityName}`;
  }

  getImportedItems() {
    let import$: Observable<PanelEntity[]>;
    if (this.liveEventId) {
      import$ = this.liveEventService.getQuestionsForEvent({
        id: this.liveEventId,
      });
    } else if (this.examPlayId) {
      import$ = this.exams.getQuestionsForExam({ id: this.examPlayId });
    } else if (this.convertToEvent) {
      import$ = from([]);
    } else {
      switch (this.input.type) {
        case PanelEntityType.Quiz:
          if (this.bookId) {
            import$ = this.books.getQuizForBook({
              id: this.input.parentId,
            });
          } else {
            import$ =
              this.input?.parentId && !_.isNaN(this.input?.parentId)
                ? this.courses.getQuizInCourse({
                    id: this.input.parentId,
                  })
                : of(null);
          }
          break;
        case PanelEntityType.Event:
          if (this.bookId) {
            import$ = this.books.getEventsInBook({
              id: this.input.parentId,
            });
          } else {
            import$ = this.courses.getEventsInCourse({
              id: this.input.parentId,
            });
          }
          break;
        case PanelEntityType.Exam:
          import$ =
            this.input?.parentId && !_.isNaN(this.input?.parentId)
              ? this.courses.getExamsInCourse({
                  id: this.input.parentId,
                })
              : of(null);
          break;
        case PanelEntityType.Question:
          import$ = this.quizzes.getQuestionsForQuiz({
            id: this.input.parentId,
          });
          break;
        case PanelEntityType.FlashCardStack:
          if (this.bookId) {
            import$ = this.books.getCardStacksInBook({
              id: this.input.parentId,
            });
          } else {
            import$ =
              this.input?.parentId && !_.isNaN(this.input?.parentId)
                ? this.courses.getCardStacksInCourse({
                    id: this.input.parentId,
                  })
                : of(null);
          }
          break;
        case PanelEntityType.FlashCard:
          import$ = this.flashCardStacks.getCardsInFlashCardStack({
            id: this.input.parentId,
          });
          break;
        case PanelEntityType.ExamQuestion:
          import$ = this.exams.getQuestionsForExam({
            id: this.input.parentId,
          });
          break;
        case PanelEntityType.EventQuestion:
          import$ = this.liveEventService.getQuestionsForEvent({
            id: this.input.parentId,
          });
          break;
        default:
          throw new Error();
      }
    }
    import$.subscribe({
      next: (res) => {
        this.importedItems = res;
        this.selectedRows = JSON.parse(JSON.stringify(res));
      },
    });
  }

  importContent() {
    if (
      this.input.type === PanelEntityType.Event ||
      this.input.type === PanelEntityType.Exam
    ) {
      this.import(false);
      return;
    }
    const data = {
      link: {
        title: 'importItemDialog.linkContent',
        text: 'importItemDialog.changeSync',
      },
      copy: {
        title: 'importItemDialog.copyContent',
        text: 'importItemDialog.changeNoSync',
      },
    };
    if (this.input.type === PanelEntityType.Quiz) {
      data.link = {
        title: 'importItemDialog.linkQuizContent',
        text: 'importItemDialog.changeQuizSync',
      };
      data.copy = {
        title: 'importItemDialog.copyQuizContent',
        text: 'importItemDialog.changeQuizNoSync',
      };
    }
    if (this.input.type === PanelEntityType.FlashCardStack) {
      data.link = {
        title: 'importItemDialog.linkStackContent',
        text: 'importItemDialog.changeStackSync',
      };
      data.copy = {
        title: 'importItemDialog.copyStackContent',
        text: 'importItemDialog.changeStackNoSync',
      };
    }
    this.matDialog
      .open<CopyLinkDialogComponent>(CopyLinkDialogComponent, {
        data,
        autoFocus: false,
      })
      .afterClosed()
      .subscribe((createCopy: string) => {
        if (createCopy) {
          this.import(createCopy === 'copy' ? true : false);
        }
      });
  }

  import(createCopy: boolean) {
    this.loading = true;
    let import$: Observable<any>;
    if (this.liveEventId) {
      if (createCopy) {
        import$ = this.liveEventService.copyQuestionToEvent({
          id: this.liveEventId,
          body: this.selectedItemsToImport as QuestionModel[],
        });
      } else {
        import$ = this.liveEventService.linkQuestionToEvent({
          id: this.liveEventId,
          body: this.selectedItemsToImport as QuestionModel[],
        });
      }
    } else if (this.examPlayId) {
      if (createCopy) {
        import$ = this.exams.copyQuestionToExam({
          id: this.examPlayId,
          body: this.selectedItemsToImport as QuestionModel[],
        });
      } else {
        import$ = this.exams.linkQuestionToExam({
          id: this.examPlayId,
          body: this.selectedItemsToImport as QuestionModel[],
        });
      }
    } else if (this.convertToEvent) {
      import$ = convertQuizToEvent(
        this.selectedItemsToImport[0] as QuizModel,
        this.quizzes,
        this.liveEventService
      ).pipe(
        tap(({ event }: { event: LiveEvent }) => {
          this.liveEventId = event.id;
        })
      );
    } else {
      switch (this.input.type) {
        case PanelEntityType.Quiz:
          if (createCopy) {
            if (this.bookId) {
              import$ = this.books.copyQuizInBook({
                id: this.input.parentId,
                body: this.selectedItemsToImport as QuizModel[],
              });
            } else {
              import$ = this.courses.copyQuizToCourse({
                id: this.input.parentId,
                body: this.selectedItemsToImport as QuizModel[],
                copyQuestions: true,
              });
            }
          } else {
            if (this.bookId) {
              import$ = this.books.linkQuizToBook({
                id: this.input.parentId,
                body: this.selectedItemsToImport as QuizModel[],
              });
            } else {
              import$ = this.courses.linkQuizInCourse({
                id: this.input.parentId,
                body: this.selectedItemsToImport as QuizModel[],
              });
            }
          }
          break;
        case PanelEntityType.Event:
          if (this.bookId) {
            import$ = forkJoin(
              this.selectedItemsToImport.map((item) =>
                this.books.addEventToBook({
                  id: this.input.parentId,
                  body: item as LiveEvent,
                })
              )
            );
          } else {
            import$ = forkJoin(
              this.selectedItemsToImport.map((item) =>
                this.courses.addEventToCourse({
                  id: this.input.parentId,
                  body: item as LiveEvent,
                })
              )
            );
          }
          break;
        case PanelEntityType.Exam:
          import$ = forkJoin(
            this.selectedItemsToImport.map((item) =>
              this.courses.addExamToCourse({
                id: this.input.parentId,
                body: item as Exam,
              })
            )
          );
          break;
        case PanelEntityType.Question:
          if (createCopy) {
            import$ = this.quizzes.copyQuestionToQuiz({
              id: this.input.parentId,
              body: this.selectedItemsToImport as QuestionModel[],
            });
          } else {
            import$ = this.quizzes.linkQuestionToQuiz({
              id: this.input.parentId,
              body: this.selectedItemsToImport as QuestionModel[],
            });
          }
          break;
        case PanelEntityType.FlashCardStack:
          if (createCopy) {
            if (this.bookId) {
              import$ = this.books.copyCardStackToBook({
                id: this.input.parentId,
                body: this.selectedItemsToImport as FlashCardStackModel[],
                copyCards: true,
              });
            } else {
              import$ = this.courses.copyCardStackToCourse({
                id: this.input.parentId,
                body: this.selectedItemsToImport as FlashCardStackModel[],
                copyCards: true,
              });
            }
          } else {
            if (this.bookId) {
              import$ = this.books.linkCardStackToBook({
                id: this.input.parentId,
                body: this.selectedItemsToImport as FlashCardStackModel[],
              });
            } else {
              import$ = this.courses.linkCardStackToCourse({
                id: this.input.parentId,
                body: this.selectedItemsToImport as FlashCardStackModel[],
              });
            }
          }
          break;
        case PanelEntityType.FlashCard:
          if (createCopy) {
            import$ = this.flashCardStacks.copyCardsToFlashCardStack({
              id: this.input.parentId,
              body: this.selectedItemsToImport as FlashCardModel[],
            });
          } else {
            import$ = this.flashCardStacks.linkCardsToFlashCardStack({
              id: this.input.parentId,
              body: this.selectedItemsToImport as FlashCardModel[],
            });
          }
          break;
        case PanelEntityType.ExamQuestion:
          if (createCopy) {
            import$ = this.exams.copyQuestionToExam({
              id: this.input.parentId,
              body: this.selectedItemsToImport as QuestionModel[],
            });
          } else {
            import$ = this.exams.linkQuestionToExam({
              id: this.input.parentId,
              body: this.selectedItemsToImport as QuestionModel[],
            });
          }
          break;
        case PanelEntityType.EventQuestion:
          if (createCopy) {
            import$ = this.liveEventService.copyQuestionToEvent({
              id: this.input.parentId,
              body: this.selectedItemsToImport as QuestionModel[],
            });
          } else {
            import$ = this.liveEventService.linkQuestionToEvent({
              id: this.input.parentId,
              body: this.selectedItemsToImport as QuestionModel[],
            });
          }
          break;
        default:
          throw new Error('Error: ' + this.input.type);
      }
    }
    import$.subscribe({
      next: () => {
        this.goBack();
      },
    });
  }

  goBack() {
    if (this.liveEventId) {
      if (!this.courseId && !this.bookId) {
        this.router.navigate(['/cms/live-events', this.liveEventId]);
      } else {
        this.location.back();
      }
      return;
    }
    if (this.examPlayId) {
      this.router.navigate(['/cms/exams', this.examPlayId]);
      return;
    }
    let entity;
    if (this.input.type === PanelEntityType.Question) {
      entity = 'quiz';
    } else if (this.input.type === PanelEntityType.FlashCard) {
      entity = 'flash-card-stack';
    } else if (this.input.type === PanelEntityType.ExamQuestion) {
      entity = 'exam';
    } else if (this.input.type === PanelEntityType.EventQuestion) {
      entity = 'events';
    }
    if (entity === 'quiz' && !this.isParentIds) {
      this.router.navigate([`cms/content/${this.input.parentId}/questions`]);
      return;
    }
    const queryParams = this.courseId
      ? {
          courseId: this.courseId,
        }
      : this.bookId
      ? { bookId: this.bookId }
      : {};
    if (entity === 'exam') {
      this.router.navigate([`cms/exams/${this.input.parentId}`], {
        queryParams,
      });
      return;
    }
    if (entity === 'flash-card-stack' && !this.courseId && !this.bookId) {
      this.router.navigate([`cms/content/${this.input.parentId}/flash-cards`]);
      return;
    }
    if (entity === 'events') {
      this.router.navigate([`cms/live-events/${this.input.parentId}`], {
        queryParams,
      });
      return;
    }
    if (entity === PanelEntityType.FlashCard && !this.isParentIds) {
      this.router.navigateByUrl(
        `/cms/content/${this.input.parentId}/flash-cards?from=stack`
      );
      return;
    }
    if (entity) {
      if (this.bookId) {
        this.router.navigate([
          `/cms/books/edit/${this.bookId}`,
          {
            book: this.bookId,
            [entity]: this.input.parentId,
          },
        ]);
      } else {
        this.router.navigate([
          `/cms/courses/edit/${this.courseId}`,
          {
            course: this.courseId,
            [entity]: this.input.parentId,
          },
        ]);
      }
    } else {
      if (this.isParentEntity) {
        if (this.bookId) {
          this.router.navigateByUrl(
            `/cms/books/edit/${this.bookId}?tab=${this.input?.type}`
          );
        } else {
          this.router.navigateByUrl(
            `/cms/courses/edit/${this.courseId}?tab=${this.input?.type}`
          );
        }
      } else {
        if (this.bookId) {
          this.router.navigateByUrl(`/cms/books/edit/${this.bookId}`);
        } else if (this.courseId) {
          this.router.navigateByUrl(`/cms/courses/edit/${this.courseId}`);
        } else if (this.convertToEvent) {
          this.router.navigateByUrl(`/cms/live-events`);
        }
      }
    }
  }

  get isParentIds() {
    return this.courseId || this.bookId;
  }

  get isParentEntity() {
    const entities = [
      PanelEntityType.Exam,
      PanelEntityType.Event,
      PanelEntityType.FlashCardStack,
    ];
    return entities.includes(this.input?.type);
  }

  addEntity() {
    if (this.selectedItem) {
      this.selectedItemsToImport = [
        this.selectedItem,
        ...this.selectedItemsToImport,
      ];
      this.selectedItem = undefined;
      this.isShowingLargeJoinInfo = false;
    }
  }
}
